import { useIM } from '@wui/im'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { mockTranscribeVoiceMessage } from '../api/mock-transcribe-voice-message'
import { deleteVoiceDraft, loadVoiceDraft, saveVoiceDraft } from '../api/voice-draft-db'
import { clampVoicePlaybackRate } from '../lib/clamp-voice-playback-rate'
import { extractPeaksFromBlob } from '../lib/extract-audio-peaks'
import { isVoiceRecordingInfo } from '../lib/is-voice-recording-info'

import {
  VOICE_PLAYBACK_RATE_STORAGE_KEY,
  VOICE_VOLUME_STORAGE_KEY,
  VoiceRecorderStatus,
} from './constants'
import { VoiceDraftSession, VoicePlaybackState, VoiceRecordingInfoPayload } from './types'

const INDICATOR_TIMEOUT_MS = 4000

const readStoredNumber = (key: string, fallback: number): number => {
  if (typeof localStorage === 'undefined') return fallback

  const raw = localStorage.getItem(key)
  const parsed = raw ? Number(raw) : Number.NaN
  return Number.isFinite(parsed) ? parsed : fallback
}

export const useVoiceMessageStore = defineStore('voice-message', () => {
  const sessions = ref<Map<string, VoiceDraftSession>>(new Map())
  const remoteRecordingsByPServed = ref<Map<string, Map<string, number>>>(new Map())
  const playback = ref<VoicePlaybackState | null>(null)
  const playbackRate = ref(clampVoicePlaybackRate(readStoredNumber(VOICE_PLAYBACK_RATE_STORAGE_KEY, 1)))
  const volume = ref(readStoredNumber(VOICE_VOLUME_STORAGE_KEY, 1))
  const transcripts = ref(new Map<string, string>())
  const transcribingCookies = ref(new Set<string>())
  const expandedTranscripts = ref(new Set<string>())

  const remoteTimeouts = new Map<string, Map<string, ReturnType<typeof setTimeout>>>()

  const getSession = (pServed: string): VoiceDraftSession | undefined => {
    return sessions.value.get(pServed)
  }

  const upsertSession = (session: VoiceDraftSession) => {
    sessions.value.set(session.pServed, { ...session })
    sessions.value = new Map(sessions.value)
  }

  const removeSession = (pServed: string) => {
    sessions.value.delete(pServed)
    sessions.value = new Map(sessions.value)
    void deleteVoiceDraft(pServed)
  }

  const persistDraft = (session: VoiceDraftSession) => {
    if (!session.blob) return

    void saveVoiceDraft({
      pServed: session.pServed,
      roomId: session.roomId,
      mimeType: session.mimeType,
      durationMs: session.durationMs,
      peaks: session.peaks,
      blob: session.blob,
    })
  }

  const restoreDraft = async (pServed: string): Promise<VoiceDraftSession | undefined> => {
    const existing = getSession(pServed)
    if (existing) return existing

    try {
      const stored = await loadVoiceDraft(pServed)
      if (!stored) return undefined

      const session: VoiceDraftSession = {
        pServed: stored.pServed,
        roomId: stored.roomId,
        status: VoiceRecorderStatus.paused,
        durationMs: stored.durationMs,
        mimeType: stored.mimeType,
        peaks: stored.peaks,
        blob: stored.blob,
      }

      if (stored.blob) {
        try {
          const extracted = await extractPeaksFromBlob(stored.blob)
          session.peaks = extracted.peaks
          if (!session.durationMs) session.durationMs = extracted.durationMs
        } catch {
          // keep stored analyser peaks if decode fails
        }
      }

      upsertSession(session)
      return session
    } catch (error) {
      console.error(error)
      return undefined
    }
  }

  const addRemoteRecording = (pServed: string, subscriberId: string, timestamp: number) => {
    const current = remoteRecordingsByPServed.value.get(pServed) ?? new Map<string, number>()
    current.set(subscriberId, timestamp)
    remoteRecordingsByPServed.value.set(pServed, current)
    remoteRecordingsByPServed.value = new Map(remoteRecordingsByPServed.value)

    let timeouts = remoteTimeouts.get(pServed)
    if (!timeouts) {
      timeouts = new Map()
      remoteTimeouts.set(pServed, timeouts)
    }

    const previousTimeout = timeouts.get(subscriberId)
    if (previousTimeout) clearTimeout(previousTimeout)

    timeouts.set(subscriberId, setTimeout(() => {
      removeRemoteRecording(pServed, subscriberId)
    }, INDICATOR_TIMEOUT_MS))
  }

  const removeRemoteRecording = (pServed: string, subscriberId: string) => {
    const current = remoteRecordingsByPServed.value.get(pServed)
    current?.delete(subscriberId)
    remoteRecordingsByPServed.value = new Map(remoteRecordingsByPServed.value)

    const timeouts = remoteTimeouts.get(pServed)
    const timeoutId = timeouts?.get(subscriberId)
    if (timeoutId) clearTimeout(timeoutId)
    timeouts?.delete(subscriberId)
  }

  const hasRemoteRecording = (pServed: string): boolean => {
    return Boolean(remoteRecordingsByPServed.value.get(pServed)?.size)
  }

  const setPlayback = (next: VoicePlaybackState | null) => {
    playback.value = next
  }

  const setPlaybackRate = (rate: number) => {
    playbackRate.value = clampVoicePlaybackRate(rate)
    localStorage.setItem(VOICE_PLAYBACK_RATE_STORAGE_KEY, String(playbackRate.value))
  }

  const setVolume = (nextVolume: number) => {
    volume.value = nextVolume
    localStorage.setItem(VOICE_VOLUME_STORAGE_KEY, String(nextVolume))
  }

  const requestTranscript = async (fileCookie: string, text: string): Promise<string | undefined> => {
    if (!fileCookie) return undefined
    if (transcripts.value.has(fileCookie)) return transcripts.value.get(fileCookie)
    if (transcribingCookies.value.has(fileCookie)) return undefined

    transcribingCookies.value = new Set(transcribingCookies.value).add(fileCookie)

    try {
      await mockTranscribeVoiceMessage(fileCookie)
      transcripts.value = new Map(transcripts.value).set(fileCookie, text)
      expandedTranscripts.value = new Set(expandedTranscripts.value).add(fileCookie)
      return text
    } finally {
      const next = new Set(transcribingCookies.value)
      next.delete(fileCookie)
      transcribingCookies.value = next
    }
  }

  const toggleTranscript = (fileCookie: string) => {
    if (!fileCookie || !transcripts.value.has(fileCookie)) return

    const next = new Set(expandedTranscripts.value)
    if (next.has(fileCookie)) next.delete(fileCookie)
    else next.add(fileCookie)
    expandedTranscripts.value = next
  }

  const { onInfoExtendedReceived } = useIM()
  onInfoExtendedReceived<VoiceRecordingInfoPayload>((message) => {
    if (!isVoiceRecordingInfo(message.extendedParams)) return

    if (message.extendedParams.state === 'start') {
      addRemoteRecording(message.executorRoom || message.pServed, message.executorUser, message.timestamp)
      return
    }

    removeRemoteRecording(message.executorRoom || message.pServed, message.executorUser)
  })

  return {
    sessions,
    remoteRecordingsByPServed,
    playback,
    playbackRate,
    volume,
    transcripts,
    transcribingCookies,
    expandedTranscripts,
    getSession,
    upsertSession,
    removeSession,
    persistDraft,
    restoreDraft,
    addRemoteRecording,
    removeRemoteRecording,
    hasRemoteRecording,
    setPlayback,
    setPlaybackRate,
    setVolume,
    requestTranscript,
    toggleTranscript,
  }
})
